import { FormInstance, message, Modal } from 'antd';
import { contentService } from '../../service/contentService';
import { ContentStreamManifestInterface, SubtitleInterface } from '../../type';
import {
  VideoManifestUpdateResponseInterface,
  VideoManifestUpdateDetailInterface,
  UpdatedManifestInterface,
  CreatedManifestInterface,
} from '../../type/contentType';
import React from 'react';
import { UploadVideoInterface } from '../../../../shared/type/uploadVideoType';
import { originVideoUploadService } from '../service/originVideoUploadService';

interface TranscodeHandlersProps {
  id: number;
  setContent: React.Dispatch<React.SetStateAction<ContentStreamManifestInterface[]>>;
}

interface TranscodeHandlers {
  handleRefresh: () => Promise<void>;
  handleStartTranscode: () => Promise<void>;
  handleRetranscodeWithEmbeddedSubtitle: () => Promise<void>;
  handleUpdateVideoManifest: () => Promise<void>;
  handleSaveButtonClick: (
    contentId: number,
    form: FormInstance,
    uploadVideo?: UploadVideoInterface,
    onProgress?: (videoFile: UploadVideoInterface) => void
  ) => Promise<void>;
}

const transcodeHandlers = ({ id, setContent }: TranscodeHandlersProps): TranscodeHandlers => {
  const handleRefresh = async (): Promise<void> => {
    const content = await contentService.fetchById(id);
    setContent(content.content_stream_manifests || []);
  };

  const handleStartTranscode = async (): Promise<void> => {
    await contentService.videoTranscode(id);
    message.success('Transcode started');
  };

  const handleRetranscodeWithEmbeddedSubtitle = async (): Promise<void> => {
    Modal.confirm({
      title: 'Retranscode with embedded subtitle',
      content: 'Are you sure you want to retranscode with embedded subtitle?',
      onOk: async () => {
        try {
          await contentService.videoTranscodeWithEmbeddedSubtitle(id);
          message.success('Retranscode with embedded subtitle started');
        } catch (error: unknown) {
          console.error('Retranscode with embedded subtitle failed:', error);
        }
      },
    });
  };

  const handleUpdateVideoManifest = async (): Promise<void> => {
    // Common function to display result modal
    const showResultModal = (result: VideoManifestUpdateResponseInterface) => {
      Modal.info({
        title: 'Video URL Refresh Results',
        width: 800,
        maskClosable: false,
        closable: true,
        content: (
          <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
            <div
              style={{
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: '#f6f6f6',
                borderRadius: '6px',
              }}
            >
              <strong>Overall Result: </strong>
              {result.message}
            </div>
            <div style={{ marginBottom: '16px' }}>
              <span style={{ color: '#52c41a', fontWeight: 'bold' }}>Success: {result.success}</span>
              {' | '}
              <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>Failed: {result.failed}</span>
            </div>

            {result.details.map((detail: VideoManifestUpdateDetailInterface, index: number) => (
              <div
                key={index}
                style={{
                  marginBottom: '20px',
                  padding: '16px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '6px',
                  backgroundColor: detail.success ? '#f6ffed' : '#fff2f0',
                }}
              >
                <div style={{ marginBottom: '8px' }}>
                  <strong>Content:</strong> {detail.content_title} (ID: {detail.content_id})
                </div>
                <div
                  style={{
                    marginBottom: '8px',
                    color: detail.success ? '#52c41a' : '#ff4d4f',
                    fontWeight: 'bold',
                  }}
                >
                  Status: {detail.success ? '✅ Success' : '❌ Failed'}
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <strong>Message:</strong> {detail.message}
                </div>

                {detail.updated_manifests && detail.updated_manifests.length > 0 && (
                  <div style={{ marginBottom: '12px' }}>
                    <strong>Updated Manifests:</strong>
                    {detail.updated_manifests.map(
                      (manifest: UpdatedManifestInterface, idx: number) => (
                        <div
                          key={idx}
                          style={{
                            marginTop: '8px',
                            padding: '8px',
                            backgroundColor: '#fafafa',
                            borderRadius: '4px',
                            fontSize: '12px',
                            color: '#666',
                          }}
                        >
                          <div>
                            <strong>Resolution:</strong> {manifest.preset}
                          </div>
                          <div style={{ marginTop: '4px' }}>
                            <strong>Previous URL:</strong>
                            <div style={{ wordBreak: 'break-all', color: '#666' }}>
                              {manifest.old_url}
                            </div>
                          </div>
                          <div style={{ marginTop: '4px' }}>
                            <strong>New URL:</strong>
                            <div style={{ wordBreak: 'break-all', color: '#1890ff' }}>
                              {manifest.new_url}
                            </div>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                )}

                {detail.created_manifests && detail.created_manifests.length > 0 && (
                  <div>
                    <strong>Created Manifests:</strong>
                    {detail.created_manifests.map(
                      (manifest: CreatedManifestInterface, idx: number) => (
                        <div
                          key={idx}
                          style={{
                            marginTop: '8px',
                            padding: '8px',
                            backgroundColor: '#f0f9ff',
                            borderRadius: '4px',
                            fontSize: '12px',
                            color: '#666',
                          }}
                        >
                          <div>
                            <strong>Resolution:</strong> {manifest.preset}
                          </div>
                          <div style={{ marginTop: '4px' }}>
                            <strong>New URL:</strong>
                            <div style={{ wordBreak: 'break-all', color: '#1890ff' }}>
                              {manifest.new_url}
                            </div>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        ),
        onOk: () => {
          const refreshData = async () => {
            const content = await contentService.fetchById(id);
            setContent(content.content_stream_manifests || []);
          };
          refreshData();
        },
      });
    };

    Modal.confirm({
      title: 'Refresh HLS Info from ODK',
      content: 'This will re-fetch HLS information from ODK. Do you want to proceed?',
      onOk: async () => {
        try {
          const result = await contentService.updateVideoManifest(id);
          showResultModal(result);
        } catch (error: unknown) {
          console.log(error);

          // When 400 error, parse response data and use the same modal
          if (
            error &&
            typeof error === 'object' &&
            'response' in error &&
            error.response &&
            typeof error.response === 'object' &&
            'status' in error.response &&
            error.response.status === 400 &&
            'data' in error.response
          ) {
            // 400 error comes with normal response structure from backend, use same modal
            showResultModal(error.response.data as VideoManifestUpdateResponseInterface);
          } else {
            console.error('Failed to refresh video URL:', error);
          }
        }
      },
    });
  };

  const handleSaveButtonClick = async (
    contentId: number,
    form: FormInstance,
    uploadVideo?: UploadVideoInterface,
    onProgress?: (videoFile: UploadVideoInterface) => void
  ): Promise<void> => {
    const loadingMessage = message.loading('Subtitle upload...', 0);
    const subtitleList = form.getFieldValue('subtitleList');
    if (!subtitleList) {
      Modal.error({ title: 'Error', content: 'No subtitle list found', okText: 'Confirm' });
      loadingMessage();
    } else {
      const uploadSubtitleList = subtitleList.filter(
        (subtitle: SubtitleInterface) => subtitle.id < 0
      );
      const filteredSubtitleList = uploadSubtitleList.filter(
        (subtitle: SubtitleInterface) => subtitle.lang !== '' && subtitle.file !== undefined
      );

      if (filteredSubtitleList.length > 0) {
        try {
          await contentService.uploadSubtitle(contentId, filteredSubtitleList);
        } catch (error) {
          console.log(error);
        }
        message.success('Subtitle upload success');
      }

      const updateSubtitleList = subtitleList.filter(
        (subtitle: SubtitleInterface) => subtitle.id > 0
      );

      if (updateSubtitleList.length > 0) {
        try {
          await contentService.updateSubtitle(contentId, updateSubtitleList);
        } catch (error) {
          console.log(error);
        }
        message.success('Subtitle update success');
        form.resetFields(['subtitleList']);
      }

      loadingMessage();
    }

    if (uploadVideo) {
      const loadingMessage = message.loading('Video upload...', 0);
      const uploadResult = await originVideoUploadService.uploadVideoFiles(
        [uploadVideo].map(file => {
          return {
            ...file,
            title: file.name,
            state: 'normal',
          };
        }) as UploadVideoInterface[],
        false,
        contentId,
        onProgress
      );

      if (uploadResult) {
        loadingMessage();
        // window.location.reload();
      }
      loadingMessage();
    }
  };

  return {
    handleRefresh,
    handleStartTranscode,
    handleRetranscodeWithEmbeddedSubtitle,
    handleUpdateVideoManifest,
    handleSaveButtonClick,
  };
};

export default transcodeHandlers;
